import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchWorkshops } from '../api/workshops';
import { Workshop } from '../types/workshops';

const STORAGE_KEY = 'van-check.selectedOrganisationId';

type WorkshopContextValue = {
  workshops: Workshop[];
  selectedOrganisationId: string;
  selectedWorkshop: Workshop | undefined;
  isLoading: boolean;
  setSelectedOrganisationId: (id: string) => void;
};

const WorkshopContext = createContext<WorkshopContextValue | undefined>(undefined);

const readStoredOrganisationId = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
};

const writeStoredOrganisationId = (id: string) => {
  try {
    if (id) {
      localStorage.setItem(STORAGE_KEY, id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
  }
};

export const WorkshopProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth0();
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [selectedOrganisationId, setSelectedOrganisationIdState] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const setSelectedOrganisationId = useCallback((id: string) => {
    setSelectedOrganisationIdState(id);
    writeStoredOrganisationId(id);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || isAuthLoading) {
      return;
    }

    const loadWorkshops = async () => {
      setIsLoading(true);
      try {
        const list = (await fetchWorkshops()) ?? [];
        setWorkshops(list);

        const storedId = readStoredOrganisationId();
        const hasAccess = list.some((w) => w.organisationId === storedId);

        const nextId = hasAccess ? storedId : (list[0]?.organisationId ?? '');

        setSelectedOrganisationId(nextId);

      } catch (error) {
        console.error('Error with fetching workshops:', error);
        setWorkshops([]);
        setSelectedOrganisationId('')
      } finally {
        setIsLoading(false);
      }
    };

    loadWorkshops();
  }, [isAuthenticated, isAuthLoading, setSelectedOrganisationId]);

  const selectedWorkshop = useMemo(
    () => workshops.find((workshop) => workshop.organisationId === selectedOrganisationId),
    [workshops, selectedOrganisationId]
  );

  const value = useMemo(
    () => ({
      workshops,
      selectedOrganisationId,
      selectedWorkshop,
      isLoading,
      setSelectedOrganisationId,
    }),
    [workshops, selectedOrganisationId, selectedWorkshop, isLoading, setSelectedOrganisationId]
  );

  return <WorkshopContext.Provider value={value}>{children}</WorkshopContext.Provider>;
};

export const useSelectedWorkshop = () => {
  const context = useContext(WorkshopContext);
  if (!context) {
    throw new Error('useSelectedWorkshop must be used within a WorkshopProvider');
  }
  return context;
};
